/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { type ActionCloseArgs, type BaseRequestOptionsArgs, type HttpResponse, HttpService, PromptService } from '@dm/core';
import { type ServiceNis } from 'app/common/types/mo';
import { type Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { ServiceNisStrService } from './service-nis-str.service';

@Injectable({ providedIn: 'root' })
export class ServiceNisService {
    constructor(
        private http: HttpService,
        private str: ServiceNisStrService,
        private promptService: PromptService,
    ) {}

    /**
     * 适配从接口查询回来的数据
     * @param data
     */
    adaptData(data: ServiceNis): void {
        if (data.NISSERVERADDR) {
            const ips = JSON.parse(data.NISSERVERADDR);
            data.primaryIp = ips[0];
            data.standbyIp1 = ips[1];
            data.standbyIp2 = ips[2];
        }
    }

    /**
     * 查询基础设置
     */
    get(vstoreId: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('serviceNis.get');
        rest.setParams({
            vstoreId,
        });
        return this.http.get(rest, options)
            .pipe(
                tap(({ data }) => {
                    this.adaptData(data);
                }),
            );
    }

    /**
     * 修改基础设置
     */
    modify(vstoreId: string, oriParams: any, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        let params = oriParams;
        const rest = this.http.makeUrl('serviceNis.put');
        params = {
            ...params,
            vstoreId,
        };
        return this.http.put(rest, params, options);
    }

    /**
     * 初始化配置
     */
    delete(vstoreId: string, restOptions?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('serviceNis.delete');
        const options = {
            ...restOptions,
            body: {
                vstoreId,
            },
        };
        return this.http.delete(rest, options);
    }

    /**
     * 恢复初始配置
     */
    reset(vstoreId: string, actionClose: (args: ActionCloseArgs) => void): void {
        this.promptService.warning({
            id: 'prompt-service-nis-reset',
            title: this.str.i18n.resetBtn,
            content: this.str.i18n.resetMsg,
            onOk: () => {
                this.promptService.close();
                this.delete(vstoreId, { mask: true, doSuccess: true })
                    .subscribe(() => {
                        actionClose({ refresh: true });
                    }, () => {
                        actionClose({ refresh: false });
                    });
            },
        });
    }

    /**
     * 测试服务器地址
     */
    test(vstoreId: string, address: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('serviceNis.test');
        const params = {
            vstoreId,
            NISSERVERADDR: JSON.stringify([address]),
        };
        return this.http.put(rest, params, options);
    }
}
